import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { AllocationStatusFilters, AllocationStatusTable, Button, LoadingSpinner, ReAllocateModal } from '../components'
import { API_BASE_URL, LoadingSpinnerMeta } from '../data'
import {
    AllocationStatusCounts,
    AllocationStatusFilters as FilterType,
    AllocationStatusItem,
    AllocationStatusPageMetaProps,
    Area,
    Event,
    Shift,
} from '../data/types'
import { ChevronLeftIcon, ChevronRightIcon, DownloadIcon, XIcon } from '../icons'
import { useAuth } from './auth'
import { ShiftMoveModal } from '../components/ShiftMoveModal'
import { ShiftMoveModalMeta } from '../data'
import { VolunteerProfileModal } from '../components'

const DEFAULT_FILTERS: FilterType = {
    status: null,
    event_id: null,
    area_id: null,
    shift_id: null,
    volunteer_search: '',
    decline_reason: [],
    date_from: '',
    date_to: '',
    offered_as_tkn: null,
    min_pending_count: null,
    max_pending_count: null,
    min_accepted_count: null,
    max_accepted_count: null,
    min_declined_count: null,
    max_declined_count: null,
    count_filter_event_ids: [],
    sort_by: 'updated_at',
    sort_dir: 'desc',
    startTime: '',
    endTime: '',
    review: false
}

const ITEMS_PER_PAGE = 100

type PaginationData = {
    current_page: number
    last_page: number
    per_page: number
    total: number
}

type Props = {
    meta: AllocationStatusPageMetaProps
}

type MoveAllocationResponse = {
    message?: string
    moved_allocation_ids?: number[]
    skipped_volunteers?: Array<{
        allocation_id: number
        csid: number
        name: string
    }>
}

export const AllocationStatus = (props: Props) => {
    const { meta } = props
    const { user } = useAuth()

    const [loading, setLoading] = useState(true)
    const [allocations, setAllocations] = useState<AllocationStatusItem[]>([])
    const [counts, setCounts] = useState<AllocationStatusCounts>({ pending: 0, accepted: 0, declined: 0, total: 0 })
    const [error, setError] = useState<string | null>(null)

    // Filter data
    const [events, setEvents] = useState<Event[]>([])
    const [areas, setAreas] = useState<Area[]>([])
    const [shifts, setShifts] = useState<Shift[]>([])

    const [isShiftMoveModalOpen, setIsShiftMoveModalOpen] = useState(false);
    const [selectedAlloactions, setSelectedAllocations] = useState<AllocationStatusItem[]>([]);
    const [selectedAllocation, setSelectedAllocation] = useState<AllocationStatusItem | null>(null);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

    // Current filters
    const [filters, setFilters] = useState<FilterType>(DEFAULT_FILTERS)

    // Pagination
    const [pagination, setPagination] = useState<PaginationData>({
        current_page: 1,
        last_page: 1,
        per_page: ITEMS_PER_PAGE,
        total: 0
    })

    // Selection state for bulk actions
    const [selectedIds, setSelectedIds] = useState<number[]>([])

    // Re-allocate modal state
    const [isReAllocateModalOpen, setIsReAllocateModalOpen] = useState(false)
    const [successMessage, setSuccessMessage] = useState<string | null>(null)

    // Export state
    const [isExporting, setIsExporting] = useState(false)

    // Bulk update state
    const [isBulkUpdating, setIsBulkUpdating] = useState(false)

    const hasFetched = useRef(false)

    // Fetch events for filter dropdown
    useEffect(() => {
        if (hasFetched.current) return
        hasFetched.current = true

        const fetchInitialData = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/event`, { credentials: 'include' })
                if (response.ok) {
                    const result = await response.json()
                    let eventData = result.value || result
                    if (Array.isArray(eventData) && eventData.length > 0 && Array.isArray(eventData[0])) {
                        eventData = eventData[0]
                    }
                    setEvents(eventData)
                }
            } catch (err) {
                console.error('Error fetching events:', err)
            }

            // Fetch initial allocations
            fetchAllocations(1, DEFAULT_FILTERS)
        }

        fetchInitialData()
    }, [])

    // Fetch areas when event changes
    useEffect(() => {
        if (filters.event_id) {
            const fetchAreas = async () => {
                try {
                    const response = await fetch(`${API_BASE_URL}/area/${filters.event_id}`, { credentials: 'include' })
                    if (response.ok) {
                        const result = await response.json()
                        // API returns areas wrapped in an array
                        let areaData = result
                        if (Array.isArray(areaData) && areaData.length > 0 && Array.isArray(areaData[0])) {
                            areaData = areaData[0]
                        } else if (Array.isArray(areaData) && areaData.length === 1 && Array.isArray(areaData[0])) {
                            areaData = areaData[0]
                        } else if (Array.isArray(areaData) && areaData.length > 0 && !Array.isArray(areaData[0]) && typeof areaData[0] === 'object') {
                            // Already flat array of objects
                        } else if (Array.isArray(areaData) && areaData.length === 1) {
                            areaData = areaData[0]
                        }
                        setAreas(Array.isArray(areaData) ? areaData : [])
                    }
                } catch (err) {
                    console.error('Error fetching areas:', err)
                }
            }
            fetchAreas()
        } else {
            setAreas([])
            setFilters(prev => ({ ...prev, area_id: null, shift_id: null }))
        }
    }, [filters.event_id])

    // Fetch shifts when area changes
    useEffect(() => {
        if (filters.area_id) {
            const fetchShifts = async () => {
                try {
                    const response = await fetch(`${API_BASE_URL}/shift/${filters.area_id}`, { credentials: 'include' })
                    if (response.ok) {
                        const result = await response.json()
                        // API returns shifts wrapped in an array
                        let shiftData = result
                        if (Array.isArray(shiftData) && shiftData.length > 0 && Array.isArray(shiftData[0])) {
                            shiftData = shiftData[0]
                        } else if (Array.isArray(shiftData) && shiftData.length === 1 && Array.isArray(shiftData[0])) {
                            shiftData = shiftData[0]
                        } else if (Array.isArray(shiftData) && shiftData.length > 0 && !Array.isArray(shiftData[0]) && typeof shiftData[0] === 'object') {
                            // Already flat array of objects
                        } else if (Array.isArray(shiftData) && shiftData.length === 1) {
                            shiftData = shiftData[0]
                        }
                        setShifts(Array.isArray(shiftData) ? shiftData : [])
                    }
                } catch (err) {
                    console.error('Error fetching shifts:', err)
                }
            }
            fetchShifts()
        } else {
            setShifts([])
            setFilters(prev => ({ ...prev, shift_id: null }))
        }
    }, [filters.area_id])

    const buildQueryString = useCallback((page: number, currentFilters: FilterType) => {
        const params = new URLSearchParams()
        params.append('per_page', ITEMS_PER_PAGE.toString())
        params.append('page', page.toString())

        if (currentFilters.status) params.append('status', currentFilters.status)
        if (currentFilters.event_id) params.append('event_id', currentFilters.event_id.toString())
        if (currentFilters.area_id) params.append('area_id', currentFilters.area_id.toString())
        if (currentFilters.shift_id) params.append('shift_id', currentFilters.shift_id.toString())
        if (currentFilters.volunteer_search) params.append('volunteer_search', currentFilters.volunteer_search)
        if (currentFilters.decline_reason.length > 0) {
            currentFilters.decline_reason.forEach(reason => params.append('decline_reason[]', reason))
        }
        if (currentFilters.date_from) params.append('date_from', currentFilters.date_from)
        if (currentFilters.date_to) params.append('date_to', currentFilters.date_to)
        if (currentFilters.offered_as_tkn !== null) {
            params.append('offered_as_tkn', currentFilters.offered_as_tkn.toString())
        }
        if (currentFilters.startTime) params.append('start_time', currentFilters.startTime.toString());
        if (currentFilters.endTime) params.append('end_time', currentFilters.endTime.toString());

        // Volunteer count filters
        if (currentFilters.min_pending_count !== null) params.append('min_pending_count', currentFilters.min_pending_count.toString())
        if (currentFilters.max_pending_count !== null) params.append('max_pending_count', currentFilters.max_pending_count.toString())
        if (currentFilters.min_accepted_count !== null) params.append('min_accepted_count', currentFilters.min_accepted_count.toString())
        if (currentFilters.max_accepted_count !== null) params.append('max_accepted_count', currentFilters.max_accepted_count.toString())
        if (currentFilters.min_declined_count !== null) params.append('min_declined_count', currentFilters.min_declined_count.toString())
        if (currentFilters.max_declined_count !== null) params.append('max_declined_count', currentFilters.max_declined_count.toString())
        if (currentFilters.count_filter_event_ids.length > 0) {
            currentFilters.count_filter_event_ids.forEach(id => params.append('count_filter_event_ids[]', id.toString()))
        }
        if (currentFilters.sort_by) params.append('sort_by', currentFilters.sort_by)
        if (currentFilters.sort_dir) params.append('sort_dir', currentFilters.sort_dir)

        if (currentFilters.review !== null) params.append('review', currentFilters.review.toString());
        return params.toString()

    }, [])

    const fetchAllocations = useCallback(async (page: number, currentFilters: FilterType) => {
        setLoading(true)
        setError(null)

        try {
            const queryString = buildQueryString(page, currentFilters)
            console.log(queryString);
            const response = await fetch(`${API_BASE_URL}/allocation/status?${queryString}`, { credentials: 'include' })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            const result = await response.json()

            setAllocations(result.data || [])
            setCounts(result.counts || { pending: 0, accepted: 0, declined: 0, total: 0 })
            setPagination({
                current_page: result.meta?.current_page || 1,
                last_page: result.meta?.last_page || 1,
                per_page: result.meta?.per_page || ITEMS_PER_PAGE,
                total: result.meta?.total || 0
            })
            // Clear selection when data changes
            setSelectedIds([])
        } catch (err) {
            setError(err instanceof Error ? err.message : meta.errorMessage)
        } finally {
            setLoading(false)
        }
    }, [buildQueryString, meta.errorMessage])

    const handleFiltersChange = useCallback((newFilters: FilterType) => {
        setFilters(newFilters)
        fetchAllocations(1, newFilters)
    }, [fetchAllocations])

    const handleClearFilters = useCallback(() => {
        setFilters(DEFAULT_FILTERS)
        setAreas([])
        setShifts([])
        fetchAllocations(1, DEFAULT_FILTERS)
    }, [fetchAllocations])

    const handlePageChange = useCallback((page: number) => {
        if (page >= 1 && page <= pagination.last_page) {
            fetchAllocations(page, filters)
        }
    }, [fetchAllocations, filters, pagination.last_page])

    const handleSelectAll = useCallback((checked: boolean) => {
        if (checked) {
            setSelectedIds(allocations.map(a => a.id))
        } else {
            setSelectedIds([])
        }
    }, [allocations])

    const handleSelectOne = useCallback((id: number, checked: boolean) => {
        if (checked) {
            setSelectedIds(prev => [...prev, id])
        } else {
            setSelectedIds(prev => prev.filter(i => i !== id))
        }
    }, [])

    const handleBulkStatusUpdate = useCallback(async (newStatus: boolean | null) => {
        if (selectedIds.length === 0) return

        setIsBulkUpdating(true)
        try {
            const response = await fetch(`${API_BASE_URL}/allocation/status/bulk`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    allocation_time_ids: selectedIds,
                    status: newStatus
                })
            })

            if (!response.ok) {
                throw new Error('Failed to update statuses')
            }

            const result = await response.json()
            alert(meta.bulkUpdateSuccessMessage(result.updated_count))

            // Refresh data
            fetchAllocations(pagination.current_page, filters)
        } catch (err) {
            alert(meta.bulkUpdateErrorMessage)
        } finally {
            setIsBulkUpdating(false)
        }
    }, [selectedIds, meta, fetchAllocations, pagination.current_page, filters])

    const handleExport = useCallback(async () => {
        setIsExporting(true)
        try {
            const queryString = buildQueryString(1, filters)
            const response = await fetch(`${API_BASE_URL}/allocation/status/export?${queryString}`, {
                credentials: 'include'
            })

            if (!response.ok) {
                throw new Error('Export failed')
            }

            // Check if it's a CSV or JSON error response
            const contentType = response.headers.get('content-type')
            if (contentType?.includes('application/json')) {
                const result = await response.json()
                if (result.message) {
                    alert(result.message)
                    return
                }
            }

            // Download CSV
            const blob = await response.blob()
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `allocation_status_export_${new Date().toISOString().split('T')[0]}.csv`
            document.body.appendChild(a)
            a.click()
            window.URL.revokeObjectURL(url)
            document.body.removeChild(a)
        } catch (err) {
            console.error('Export error:', err)
            alert('Failed to export data')
        } finally {
            setIsExporting(false)
        }
    }, [buildQueryString, filters])

    const handleReAllocateSuccess = useCallback(() => {
        const count = selectedIds.length
        fetchAllocations(pagination.current_page, filters)
        setSelectedIds([])
        setSuccessMessage(meta.reAllocateSuccessMessage(count))
        // Auto-hide the message after 5 seconds
        setTimeout(() => setSuccessMessage(null), 5000)
    }, [fetchAllocations, pagination.current_page, filters, meta.reAllocateSuccessMessage, selectedIds.length]);


    const handleShiftMoveModalClick = () => {
        setIsShiftMoveModalOpen(true);
        setSelectedAllocations(allocations.filter(a => selectedIds.includes(a.id)))
    }

    const handleShiftMoveModalClickClose = () => {
        setIsShiftMoveModalOpen(false);
    }

    const isAdmin = user?.role === 'admin'
    const canReallocate = user?.permissions?.includes('manage_allocations') || user?.permissions?.includes('manage_own_allocations');


    const handleMoveShiftSubmit = async (data: {
        allocation_ids: number[],
        status: Array<boolean | null>,
        shift_id: number,
        source_shift_id?: number,
        start_time: string,
        end_time: string
    },
        movewithStatus: boolean

    ) => {

        const response = await fetch(`${API_BASE_URL}/allocation/move?with-status=${movewithStatus ? 1 : 0}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(data)
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => null)
            throw new Error(errorData?.message || 'Failed to update volunteer allocations.')
        }

        const result: MoveAllocationResponse = await response.json()
        const movedAllocationIds = result.moved_allocation_ids ?? data.allocation_ids
        const skippedVolunteers = result.skipped_volunteers ?? []
        const movedSelectedIds = allocations
            .filter(allocation => movedAllocationIds.includes(allocation.allocation_id))
            .map(allocation => allocation.id)

        // if success filter out the allocations
        setAllocations(prev => prev.filter(a => !movedAllocationIds.includes(a.allocation_id)));
        setSelectedIds(prev => prev.filter(id => !movedSelectedIds.includes(id)));

        if (skippedVolunteers.length > 0) {
            const volunteerList = skippedVolunteers
                .map(volunteer => `${volunteer.name} (${volunteer.csid})`)
                .join(', ')
            setError(`The following couldn't be moved since they are already in the target shift: ${volunteerList}`)
        } else {
            setError(null)
        }
    }

    const handleProfileSelect = (allocation: AllocationStatusItem) => {
        setSelectedAllocation(allocation);
        setIsProfileModalOpen(true);
    }


    const handleProfileSelectClose = () => {
        setSelectedAllocation(null);
        setIsProfileModalOpen(false);
    }


    const handleReviewClick = async (allocationIds: number[], status: boolean | null) => {
        try {
            const response = await fetch(`${API_BASE_URL}/allocation/review-status`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', "Accept": "application/json" },
                credentials: 'include',
                body: JSON.stringify({
                    allocation_ids: allocationIds,
                    review_status: status
                })
            });

            if (!response.ok) {
                throw new Error('Failed to update review status.');
            }


            setAllocations(prev => prev.filter(a => !allocationIds.includes(a.id)));
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Failed to update review status.");
        }
    }



    return (
        <Fragment>
            <div className="flex-1 pt-8 pb-8">
                <div className="container mx-auto px-4">
                    {/* Success message */}
                    {successMessage && (
                        <div className="mb-4 p-4 bg-success-100 dark:bg-success-900/30 border border-success-300 dark:border-success-700 rounded-lg flex items-center justify-between">
                            <p className="text-success-800 dark:text-success-200">{successMessage}</p>
                            <button
                                onClick={() => setSuccessMessage(null)}
                                className="text-success-600 hover:text-success-800 dark:text-success-400 dark:hover:text-success-200"
                            >
                                <XIcon className="w-5 h-5" />
                            </button>
                        </div>
                    )}

                    {/* Back link */}
                    <div className="mb-4">
                        <Link
                            to="/admin"
                            className="inline-flex items-center text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
                        >
                            <ChevronLeftIcon className="w-5 h-5 me-1" />
                            {meta.backLink}
                        </Link>
                    </div>

                    {/* Header */}
                    <div className="text-center mb-8">
                        <h1 className="text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                            {meta.title}
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 text-lg">
                            {meta.description}
                        </p>
                    </div>

                    {/* Error display */}
                    {error && (
                        <div className="max-w-2xl mx-auto mb-6">
                            <div className="bg-danger-100 dark:bg-danger-900 border border-danger-400 dark:border-danger-700 text-danger-700 dark:text-danger-200 px-6 py-4 rounded-lg">
                                <p>{error}</p>
                            </div>
                        </div>
                    )}

                    {/* Filters */}
                    <AllocationStatusFilters
                        meta={meta}
                        filters={filters}
                        counts={counts}
                        events={events}
                        areas={areas}
                        shifts={shifts}
                        onFiltersChange={handleFiltersChange}
                        onClearFilters={handleClearFilters}

                    />

                    {/* Actions bar */}
                    <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                        {/* Bulk actions (admin only) */}
                        <div className="flex items-center gap-2">
                            {selectedIds.length > 0 && (
                                <span className="text-sm text-gray-600 dark:text-gray-400">
                                    {meta.selectedCountLabel(selectedIds.length)}
                                </span>
                            )}
                            {selectedIds.length > 0 && isAdmin && (
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleBulkStatusUpdate(null)}
                                        disabled={isBulkUpdating}
                                    >
                                        {meta.setToPendingButton}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleBulkStatusUpdate(true)}
                                        disabled={isBulkUpdating}
                                    >
                                        {meta.setToAcceptedButton}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleBulkStatusUpdate(false)}
                                        disabled={isBulkUpdating}
                                    >
                                        {meta.setToDeclinedButton}
                                    </Button>
                                    <Button
                                        type='button'
                                        color='primary'
                                        variant='outline'
                                        size='sm'
                                        onClick={() => handleShiftMoveModalClick()}>
                                        Move Volunteer{selectedIds.length > 1 ? 's' : ''}
                                    </Button>
                                </div>
                            )}
                            {selectedIds.length > 0 && canReallocate && (
                                <Button
                                    variant="solid"
                                    size="sm"
                                    onClick={() => setIsReAllocateModalOpen(true)}
                                >
                                    {meta.reAllocateButton}
                                </Button>
                            )}
                        </div>

                        {/* Export button */}
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleExport}
                            disabled={isExporting}
                        >
                            <DownloadIcon className="w-4 h-4 me-2" />
                            {isExporting ? meta.exportingButton : meta.exportCsvButton}
                        </Button>
                    </div>

                    {/* Main content */}
                    {loading ? (
                        <div className="flex justify-center py-12">
                            <LoadingSpinner meta={LoadingSpinnerMeta} />
                        </div>
                    ) : allocations.length === 0 ? (
                        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                            {meta.noResultsMessage}
                        </div>
                    ) : (
                        <>
                            <AllocationStatusTable
                                meta={meta}
                                allocations={allocations}
                                selectedIds={selectedIds}
                                onSelectAll={handleSelectAll}
                                onSelectOne={handleSelectOne}
                                onProfileClick={handleProfileSelect}
                                onReviewClick={handleReviewClick}
                                onReAllocate={(id) => {
                                    setSelectedIds([id])
                                    setIsReAllocateModalOpen(true)
                                }}
                                canReallocate={canReallocate}
                            />

                            {/* Pagination */}
                            <div className="flex items-center justify-between mt-6">
                                <div className="text-sm text-gray-600 dark:text-gray-400">
                                    {meta.totalItemsLabel(pagination.total)}
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handlePageChange(pagination.current_page - 1)}
                                        disabled={pagination.current_page === 1}
                                    >
                                        <ChevronLeftIcon className="w-4 h-4 me-1" />
                                        {meta.previousButton}
                                    </Button>
                                    <span className="text-sm text-gray-600 dark:text-gray-400">
                                        {meta.pageLabel} {pagination.current_page} / {pagination.last_page}
                                    </span>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handlePageChange(pagination.current_page + 1)}
                                        disabled={pagination.current_page === pagination.last_page}
                                    >
                                        {meta.nextButton}
                                        <ChevronRightIcon className="w-4 h-4 ms-1" />
                                    </Button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Re-allocate Modal */}
            <ReAllocateModal
                meta={meta}
                isOpen={isReAllocateModalOpen}
                onClose={() => {
                    setIsReAllocateModalOpen(false)
                    setSelectedIds([])
                }}
                selectedIds={selectedIds}
                allocations={allocations}
                onSuccess={handleReAllocateSuccess}
            />

            <ShiftMoveModal
                meta={ShiftMoveModalMeta}
                isOpen={isShiftMoveModalOpen}
                allocations={selectedAlloactions}
                onClose={handleShiftMoveModalClickClose}
                onSubmit={handleMoveShiftSubmit}
            />

            <VolunteerProfileModal
                volunteerId={selectedAllocation?.allocation.volunteer_id || null}
                isOpen={isProfileModalOpen}
                onClose={handleProfileSelectClose}
            />

        </Fragment>
    )
}
